import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Area, Bar, CartesianGrid, ComposedChart, Line, ReferenceLine, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import { Boxes, Loader2 } from 'lucide-react';
import { api, errorMessage } from '../../lib/api';
import { fmtDate, fmtInt } from '../../lib/format';
import { cn } from '../../lib/utils';

const RANGES = [
  { days: 90, label: '3 m' },
  { days: 180, label: '6 m' },
  { days: 365, label: '1 y' },
  { days: 730, label: '2 y' },
];

const COLORS = {
  free: '#1f2937',
  onHand: '#9ca3af',
  web: '#2563eb',
  b2b: '#0d9488',
  other: '#cbd5e1',
  receipt: '#16a34a',
};

const shortDate = (iso) => new Date(iso).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' });

function Stat({ label, value, sub }) {
  return (
    <div className="min-w-0 rounded-lg border bg-card px-2.5 py-2">
      <div className="text-[10.5px] uppercase tracking-wide text-muted-foreground">{label}</div>
      <div className="truncate text-sm font-medium tabular-nums">{value}</div>
      {sub && <div className="truncate text-[11px] text-muted-foreground">{sub}</div>}
    </div>
  );
}

function Row({ label, value, color }) {
  return (
    <div className="flex justify-between gap-4">
      <span className="text-muted-foreground">{label}</span>
      <span className="font-medium tabular-nums" style={color ? { color } : undefined}>
        {value}
      </span>
    </div>
  );
}

function ChartTooltip({ active, payload, label }) {
  if (!active || !payload?.length) return null;
  const r = payload[0].payload;
  const sold = (r.web || 0) + (r.b2b || 0) + (r.other || 0);
  return (
    <div className="rounded-lg border bg-background px-3 py-2 text-xs shadow-md">
      <div className="mb-1 font-medium">{fmtDate(label)}</div>
      {r.free != null && <Row label="Free stock" value={`${fmtInt(r.free)} pcs`} />}
      {r.on_hand != null && r.on_hand !== r.free && <Row label="In the warehouse" value={`${fmtInt(r.on_hand)} pcs`} />}
      {r.reserved > 0 && <Row label="Reserved for orders" value={`${fmtInt(r.reserved)} pcs`} />}
      {sold > 0 && (
        <div className="mt-1 border-t pt-1">
          <div className="mb-0.5 text-[11px] text-muted-foreground">Sold that week</div>
          {r.web > 0 && <Row label="Webshop" value={fmtInt(r.web)} color={COLORS.web} />}
          {r.b2b > 0 && <Row label="B2B / projects" value={fmtInt(r.b2b)} color={COLORS.b2b} />}
          {r.other > 0 && <Row label="Other" value={fmtInt(r.other)} />}
        </div>
      )}
      {r.receipt && (
        <div className="mt-1 border-t pt-1" style={{ color: COLORS.receipt }}>
          Goods received{r.receipt.vendor ? ` from ${r.receipt.vendor}` : ''}
        </div>
      )}
    </div>
  );
}

export default function StockHistory({ odooId }) {
  const [days, setDays] = useState(365);
  const { data, isLoading, isFetching, error } = useQuery({
    queryKey: ['stock-history', String(odooId), days],
    queryFn: () => api.stockHistory(odooId, days),
    enabled: !!odooId,
    staleTime: 10 * 60 * 1000,
  });

  // daily stock rows; weekly sales sit on the first snapshot of their week, receipts on their day
  const series = useMemo(() => {
    if (!data?.stock?.length) return [];
    const rows = data.stock.map((p) => ({ ...p }));
    const idx = new Map(rows.map((r, i) => [r.date, i]));
    const at = (iso) => {
      if (idx.has(iso)) return rows[idx.get(iso)];
      return rows.find((r) => r.date >= iso);
    };
    for (const w of data.sales_weekly || []) {
      const r = at(w.week);
      if (r) Object.assign(r, { web: (r.web || 0) + w.web, b2b: (r.b2b || 0) + w.b2b, other: (r.other || 0) + w.other });
    }
    for (const rc of data.receipts || []) {
      const r = at(rc.date);
      if (r) r.receipt = rc;
    }
    return rows;
  }, [data]);

  const s = data?.summary || {};
  const receiptDates = series.filter((r) => r.receipt).map((r) => r.date);
  const maxSold = Math.max(1, ...series.map((r) => (r.web || 0) + (r.b2b || 0) + (r.other || 0)));
  const hasSales = (s.sold_total || 0) > 0;
  const change = s.free_start != null && s.free_now != null ? s.free_now - s.free_start : null;

  return (
    <section className="space-y-2">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h3 className="flex items-center gap-1.5 text-sm font-semibold">
          <Boxes className="h-4 w-4 text-muted-foreground" /> Free stock history
          {isFetching && !isLoading && <Loader2 className="h-3.5 w-3.5 animate-spin text-muted-foreground" />}
        </h3>
        <div className="flex rounded-md border p-0.5">
          {RANGES.map((r) => (
            <button
              key={r.days}
              type="button"
              onClick={() => setDays(r.days)}
              className={cn('rounded px-2 py-0.5 text-xs', days === r.days ? 'bg-accent font-medium text-foreground' : 'text-muted-foreground hover:text-foreground')}
            >
              {r.label}
            </button>
          ))}
        </div>
      </div>

      {isLoading && <div className="h-[240px] animate-pulse rounded-lg bg-muted/60" />}
      {error && <p className="text-sm text-destructive">{errorMessage(error)}</p>}
      {data && !series.length && (
        <p className="rounded-lg border border-dashed px-3 py-6 text-center text-sm text-muted-foreground">No stock history recorded for this item in the selected period.</p>
      )}

      {series.length > 0 && (
        <>
          <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
            <Stat
              label="Free stock now"
              value={`${fmtInt(s.free_now)} pcs`}
              sub={change != null ? `${change > 0 ? '+' : ''}${fmtInt(change)} since ${fmtDate(s.first_date)}` : undefined}
            />
            <Stat label="Highest" value={`${fmtInt(s.free_peak)} pcs`} sub="in this period" />
            <Stat
              label="Sold in period"
              value={`${fmtInt(s.sold_total)} pcs`}
              sub={hasSales ? `${fmtInt(s.sold?.web)} webshop · ${fmtInt(s.sold?.b2b)} B2B` : 'no sales'}
            />
            <Stat
              label="Unchanged for"
              value={s.days_without_change != null ? `${fmtInt(s.days_without_change)} days` : `${fmtInt(days)}+ days`}
              sub="since free stock last moved"
            />
          </div>

          <div className="rounded-lg border bg-card p-2 pr-3">
            <ResponsiveContainer width="100%" height={240}>
              <ComposedChart data={series} margin={{ top: 10, right: 4, bottom: 0, left: 0 }} barCategoryGap={0}>
                <CartesianGrid stroke="hsl(var(--border))" strokeDasharray="3 3" vertical={false} />
                <XAxis
                  dataKey="date"
                  tickFormatter={shortDate}
                  tick={{ fontSize: 11, fill: 'hsl(var(--muted-foreground))' }}
                  tickLine={false}
                  axisLine={false}
                  minTickGap={40}
                />
                <YAxis
                  yAxisId="stock"
                  allowDecimals={false}
                  tick={{ fontSize: 11, fill: 'hsl(var(--muted-foreground))' }}
                  tickLine={false}
                  axisLine={false}
                  width={40}
                />
                <YAxis
                  yAxisId="sold"
                  orientation="right"
                  hide={!hasSales}
                  domain={[0, maxSold * 3]}
                  ticks={[0, maxSold]}
                  allowDecimals={false}
                  tickFormatter={(v) => (v ? `${fmtInt(v)} sold` : '')}
                  tick={{ fontSize: 10, fill: COLORS.web }}
                  tickLine={false}
                  axisLine={false}
                  width={hasSales ? 48 : 0}
                />
                <Tooltip content={<ChartTooltip />} />
                {receiptDates.map((d) => (
                  <ReferenceLine key={d} x={d} yAxisId="stock" stroke={COLORS.receipt} strokeDasharray="3 3" />
                ))}
                {hasSales && (
                  <>
                    <Bar yAxisId="sold" dataKey="web" stackId="s" fill={COLORS.web} minPointSize={0} maxBarSize={6} isAnimationActive={false} />
                    <Bar yAxisId="sold" dataKey="b2b" stackId="s" fill={COLORS.b2b} maxBarSize={6} isAnimationActive={false} />
                    <Bar yAxisId="sold" dataKey="other" stackId="s" fill={COLORS.other} maxBarSize={6} isAnimationActive={false} />
                  </>
                )}
                <Line yAxisId="stock" type="stepAfter" dataKey="on_hand" stroke={COLORS.onHand} strokeWidth={1} strokeDasharray="3 3" dot={false} isAnimationActive={false} />
                <Area
                  yAxisId="stock"
                  type="stepAfter"
                  dataKey="free"
                  stroke={COLORS.free}
                  strokeWidth={2}
                  fill={COLORS.free}
                  fillOpacity={0.06}
                  dot={false}
                  isAnimationActive={false}
                />
              </ComposedChart>
            </ResponsiveContainer>
          </div>

          <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-[11px] text-muted-foreground">
            <Legend color={COLORS.free} label="Free stock (not reserved)" />
            <Legend color={COLORS.onHand} dashed label="In the warehouse" />
            {hasSales && <Swatch color={COLORS.web} label="Pieces sold per week (right scale): webshop" />}
            {s.sold?.b2b > 0 && <Swatch color={COLORS.b2b} label="B2B / projects" />}
            {hasSales && s.sold?.other > 0 && <Swatch color={COLORS.other} label="Other" />}
            {receiptDates.length > 0 && <Legend color={COLORS.receipt} dashed label="Goods received" />}
          </div>
        </>
      )}
    </section>
  );
}

function Legend({ color, label, dashed }) {
  return (
    <span className="inline-flex items-center gap-1.5">
      <svg width="18" height="6" aria-hidden="true">
        <line x1="0" y1="3" x2="18" y2="3" stroke={color} strokeWidth="2" strokeDasharray={dashed ? '4 3' : undefined} />
      </svg>
      {label}
    </span>
  );
}

function Swatch({ color, label }) {
  return (
    <span className="inline-flex items-center gap-1.5">
      <span className="h-2.5 w-2.5 rounded-sm" style={{ background: color }} />
      {label}
    </span>
  );
}
